import {
  ICurrencyResp,
  IPaymentGatewayReqUIFormSchema,
} from '@monetize/types/app';
import { useMemo } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
} from 'react-hook-form';
import { BsTrash } from 'react-icons/bs';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MCustomNumberInput,
  MFlex,
  MFormField,
  MText,
} from '../../../../../components/Monetize';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';

interface PaymentMethodAmountSectionProps {
  control: Control<IPaymentGatewayReqUIFormSchema>;
  errors: FieldErrors<IPaymentGatewayReqUIFormSchema>;
  configIndex: number;
  label: string;
  isEnabled: boolean;
  currencies: (ICurrencyResp & { description: string; searchTerm: string })[];
  /** When true, currency is fixed (read-only), no add/delete, and only one row is shown */
  fixedCurrency?: boolean;
}

export const PaymentMethodAmountSection = ({
  control,
  errors,
  configIndex,
  label,
  isEnabled,
  currencies,
  fixedCurrency,
}: PaymentMethodAmountSectionProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `paymentConfiguration.configurations.${configIndex}.amountConfigurations`,
  });

  const usedCurrencies = useMemo(
    () => new Set(fields.map(({ currency }) => currency)),
    [fields],
  );

  const availableCurrencies = useMemo(
    () => currencies.filter((c) => !usedCurrencies.has(c.code)),
    [currencies, usedCurrencies],
  );

  const handleAdd = () => {
    const firstAvailable = availableCurrencies[0];
    if (firstAvailable) {
      append({
        currency: firstAvailable.code,
        minimum: null,
        maximum: null,
      });
    }
  };

  const getAvailableCurrenciesForRow = (currentCurrency: string) => {
    return currencies.filter(
      (c) => !usedCurrencies.has(c.code) || c.code === currentCurrency,
    );
  };

  return (
    <MBox mt={4}>
      <MText fontSize="md" fontWeight="bold" mb={2}>
        {label}
      </MText>

      {fields.map((field, fieldIndex) => (
        <MFlex key={field.id} alignItems="center" gap={2} mb={2}>
          <MFormField
            error={
              errors.paymentConfiguration?.configurations?.[configIndex]
                ?.amountConfigurations?.[fieldIndex]?.currency
            }
            label={fieldIndex === 0 ? 'Currency' : undefined}
            w="150px"
          >
            <Controller
              name={`paymentConfiguration.configurations.${configIndex}.amountConfigurations.${fieldIndex}.currency`}
              control={control}
              render={({ field: selectField }) => (
                <MCustomSelect
                  items={getAvailableCurrenciesForRow(selectField.value)}
                  itemTitle="code"
                  itemValue="code"
                  placeholder="Currency"
                  isDisabled={!isEnabled || fixedCurrency}
                  isReadOnly={fixedCurrency}
                  {...selectField}
                />
              )}
            />
          </MFormField>

          <MFormField
            error={
              errors.paymentConfiguration?.configurations?.[configIndex]
                ?.amountConfigurations?.[fieldIndex]?.minimum
            }
            label={fieldIndex === 0 ? 'Minimum Payment Amount' : undefined}
            w="175px"
          >
            <Controller
              name={`paymentConfiguration.configurations.${configIndex}.amountConfigurations.${fieldIndex}.minimum`}
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MCustomNumberInput
                  inputMode="numeric"
                  value={value ?? ''}
                  isDisabled={!isEnabled}
                  {...rest}
                />
              )}
            />
          </MFormField>

          <MFormField
            error={
              errors.paymentConfiguration?.configurations?.[configIndex]
                ?.amountConfigurations?.[fieldIndex]?.maximum
            }
            label={fieldIndex === 0 ? 'Maximum Payment Amount' : undefined}
            w="175px"
          >
            <Controller
              name={`paymentConfiguration.configurations.${configIndex}.amountConfigurations.${fieldIndex}.maximum`}
              control={control}
              render={({ field: { value, ...rest } }) => (
                <MCustomNumberInput
                  inputMode="numeric"
                  value={value ?? ''}
                  isDisabled={!isEnabled}
                  {...rest}
                />
              )}
            />
          </MFormField>

          {!fixedCurrency && (
            <MBox pt={fieldIndex === 0 ? 7 : 0}>
              <MCustomIconButton
                variant="icon"
                icon={BsTrash}
                iconColor="tRed.base"
                isDisabled={!isEnabled}
                onClick={() => remove(fieldIndex)}
                aria-label="Remove currency configuration"
              />
            </MBox>
          )}
        </MFlex>
      ))}

      {availableCurrencies.length > 0 &&
        (!fixedCurrency || fields.length === 0) && (
          <MButton
            variant="tertiary"
            size="sm"
            isDisabled={!isEnabled}
            onClick={handleAdd}
          >
            + Add
          </MButton>
        )}
    </MBox>
  );
};
